import Button from "./Button";
import Typography from "../Typography";

/** Two-tone ring loader — colors from theme.css (--spinner-*). */
export function FormSubmitRoundSpinner({
  arcColor = "var(--spinner-arc)",
  trackColor = "var(--spinner-track)",
} = {}) {
  const r = 10;
  const c = 2 * Math.PI * r;
  const arcLen = c * 0.25;
  const gapLen = c - arcLen;

  return (
    <span className="inline-flex h-4 w-4 shrink-0" aria-hidden>
      <svg
        className="h-4 w-4 animate-spin"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r={r} stroke={trackColor} strokeWidth="2.5" fill="none" />
        <circle
          cx="12"
          cy="12"
          r={r}
          stroke={arcColor}
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${arcLen} ${gapLen}`}
          transform="rotate(-90 12 12)"
        />
      </svg>
    </span>
  );
}

/**
 * Primary action for add/edit forms: idle shows icon + label; while submitting shows the shared
 * round spinner + label (centered; full-width buttons keep content centered).
 * Use for `type="submit"` inside `<form>`, or `type="button"` with `onClick={handleSubmit(onSubmit)}` when the
 * submit control sits outside the form (e.g. sticky footer).
 */
export default function FormSubmitButton({
  loading,
  label,
  /** Text next to spinner while loading (defaults to `label`) */
  loadingText,
  idleIconClass = "ri-add-circle-line",
  variant = "primary",
  size = "md",
  className = "",
  type = "submit",
  onClick,
  disabled,
  ...rest
}) {
  const busyText = loadingText ?? label;
  const showIcon = Boolean(idleIconClass);
  const spinnerOnPrimary = variant === "primary";

  return (
    <Button
      type={type}
      variant={variant}
      size={size}
      disabled={disabled || loading}
      onClick={onClick}
      aria-busy={loading}
      className={`inline-block text-white font-bold transition-colors ${className}`.trim()}
      {...rest}
    >
      {loading ? (
        <span
          className="inline-flex w-full min-w-0 items-center justify-center gap-2"
          role="status"
        >
          <FormSubmitRoundSpinner
            arcColor={spinnerOnPrimary ? "var(--spinner-arc-on-primary)" : undefined}
            trackColor={spinnerOnPrimary ? "var(--spinner-track-on-primary)" : undefined}
          />
          <Typography variant="text2" component="span">
            {busyText}
          </Typography>
          <Typography variant="text2" className="sr-only" component="span">
            Loading...
          </Typography>
        </span>
      ) : (
        <>
          {showIcon ? <i className={idleIconClass} /> : null}
          {showIcon ? " " : null}
          {label}
        </>
      )}
    </Button>
  );
}
